import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import IosShareRoundedIcon from '@mui/icons-material/IosShareRounded';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import GroupAddOutlinedIcon from '@mui/icons-material/GroupAddOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import CheckroomOutlinedIcon from '@mui/icons-material/CheckroomOutlined';
import { copyTextToClipboard } from '@/features/guide-identity/utils/identityUrl';
import type { RewardsSummary } from '../types';
import { TRIPPY_SRC } from './PromoPostCanvas';
import { useRewardsToast } from './RewardsToast';

const GOLD = '#C8A96E';
const GOLD_SOFT = '#E9D7AE';

// Trippy's float is decorative: it switches off for reduced-motion users.
const NO_MOTION = { '@media (prefers-reduced-motion: reduce)': { animation: 'none' } };

interface TrippyHeroBannerProps {
  summary?: RewardsSummary;
  loading: boolean;
  onShare: () => void;
  onExplore: () => void;
}

function CopyRow({
  label,
  value,
  copyLabel,
  copiedMessage,
  mono,
}: {
  label: string;
  value: string;
  copyLabel: string;
  copiedMessage: string;
  mono?: boolean;
}) {
  const { t } = useTranslation('rewards');
  const notify = useRewardsToast();

  const copy = async () => {
    const ok = await copyTextToClipboard(value);
    notify(ok ? copiedMessage : t('toast.copyFailed'), ok ? 'success' : 'error');
  };

  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        component="p"
        sx={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.14em', color: GOLD_SOFT, mb: 0.5 }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          pl: 1.75,
          pr: 0.5,
          py: 0.5,
          borderRadius: 2,
          bgcolor: 'rgba(255,255,255,0.08)',
          border: '1px solid rgba(255,255,255,0.14)',
        }}
      >
        <Typography
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: '#FFFFFF',
            fontWeight: mono ? 800 : 500,
            letterSpacing: mono ? '0.06em' : 0,
            fontSize: mono ? '1.05rem' : '0.88rem',
            fontFamily: mono ? '"JetBrains Mono", ui-monospace, monospace' : undefined,
          }}
          title={value}
        >
          {value}
        </Typography>
        <Tooltip title={copyLabel}>
          <IconButton onClick={copy} aria-label={copyLabel} size="small" sx={{ color: GOLD_SOFT }}>
            <ContentCopyRoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

/**
 * The Rewards page hero: referral details on the left, Trippy on the right.
 * Uses the dashboard's sage + gold language so it reads as part of the
 * product, with a warm spotlight to lift Trippy's black tee off the green.
 */
export function TrippyHeroBanner({ summary, loading, onShare, onExplore }: TrippyHeroBannerProps) {
  const { t } = useTranslation('rewards');

  const highlights = [
    { icon: <GroupAddOutlinedIcon />, text: t('hero.highlights.referral') },
    { icon: <EventAvailableOutlinedIcon />, text: t('hero.highlights.milestones') },
    { icon: <CheckroomOutlinedIcon />, text: t('hero.highlights.represent') },
  ];

  return (
    <Box
      component="section"
      aria-labelledby="rewards-hero-title"
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 'var(--radius-xl)',
        color: '#FFFFFF',
        background:
          'radial-gradient(ellipse 55% 70% at 88% 30%, rgba(200,169,110,0.20) 0%, transparent 65%), linear-gradient(125deg, #1A2E1A 0%, #213821 50%, #2F4B2F 100%)',
        boxShadow: 'var(--shadow-lg)',
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '3fr 2fr' },
      }}
    >
      {/* Faint contour lines — a travel-map texture, kept well below the text. */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: 0.07,
          pointerEvents: 'none',
          backgroundImage: `repeating-radial-gradient(circle at 0% 100%, transparent 0 28px, ${GOLD} 29px 30px)`,
        }}
      />

      {/* ── Left: referral details ─────────────────────────────────────── */}
      <Box sx={{ position: 'relative', zIndex: 1, p: { xs: 3, sm: 4, md: 5 }, order: { xs: 2, md: 1 } }}>
        <Typography
          component="p"
          sx={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.2em', color: GOLD }}
        >
          {t('hero.eyebrow')}
        </Typography>
        <Typography
          id="rewards-hero-title"
          component="h1"
          sx={{
            mt: 1.25,
            // Global h1 styles set a dark colour; the hero sits on dark green.
            color: '#FFFFFF',
            fontFamily: '"Playfair Display", Georgia, serif',
            fontWeight: 700,
            fontSize: { xs: '1.65rem', sm: '2rem', lg: '2.35rem' },
            lineHeight: 1.15,
            letterSpacing: '-0.01em',
            maxWidth: 560,
          }}
        >
          {t('hero.headline')}
        </Typography>
        <Typography sx={{ mt: 1.5, color: 'rgba(255,255,255,0.78)', lineHeight: 1.65, maxWidth: 540, fontSize: '0.95rem' }}>
          {t('hero.body')}
        </Typography>

        <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, mt: 2.5, display: 'grid', gap: 1.1 }}>
          {highlights.map((item) => (
            <Box component="li" key={item.text} sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  flexShrink: 0,
                  borderRadius: '50%',
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: 'rgba(200,169,110,0.16)',
                  color: GOLD_SOFT,
                  '& .MuiSvgIcon-root': { fontSize: 17 },
                }}
              >
                {item.icon}
              </Box>
              <Typography sx={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.9)' }}>{item.text}</Typography>
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            mt: 3,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 0.8fr) minmax(0, 1.2fr)' },
            gap: 1.5,
          }}
        >
          {loading ? (
            <>
              <Skeleton variant="rounded" height={64} sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
              <Skeleton variant="rounded" height={64} sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
            </>
          ) : summary?.guideId && summary.referralLink ? (
            <>
              <CopyRow
                label={t('hero.guideIdLabel')}
                value={summary.guideId}
                copyLabel={t('hero.copyId')}
                copiedMessage={t('toast.idCopied')}
                mono
              />
              <CopyRow
                label={t('hero.linkLabel')}
                value={summary.referralLink}
                copyLabel={t('hero.copyLink')}
                copiedMessage={t('toast.linkCopied')}
              />
            </>
          ) : (
            <Typography sx={{ gridColumn: '1 / -1', color: 'rgba(255,255,255,0.75)', fontSize: '0.9rem' }}>
              {t('hero.noCode')}
            </Typography>
          )}
        </Box>

        <Box sx={{ mt: 3, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5 }}>
          <Button
            size="large"
            variant="contained"
            onClick={onShare}
            disabled={!summary?.referralLink}
            startIcon={<IosShareRoundedIcon />}
            sx={{
              px: 3.5,
              py: 1.4,
              borderRadius: 999,
              fontWeight: 800,
              bgcolor: GOLD,
              color: '#142214',
              boxShadow: '0 10px 28px rgba(200,169,110,0.35)',
              '&:hover': { bgcolor: GOLD_SOFT, boxShadow: '0 12px 32px rgba(200,169,110,0.45)' },
              '&.Mui-disabled': { bgcolor: 'rgba(200,169,110,0.4)', color: 'rgba(20,34,20,0.6)' },
            }}
          >
            {t('hero.share')}
          </Button>
          <Button
            size="large"
            variant="outlined"
            onClick={onExplore}
            endIcon={<ArrowDownwardRoundedIcon />}
            sx={{
              px: 3,
              py: 1.4,
              borderRadius: 999,
              fontWeight: 700,
              color: '#FFFFFF',
              borderColor: 'rgba(255,255,255,0.4)',
              '&:hover': { borderColor: '#FFFFFF', bgcolor: 'rgba(255,255,255,0.06)' },
            }}
          >
            {t('hero.explore')}
          </Button>
        </Box>
      </Box>

      {/* ── Right: Trippy ─────────────────────────────────────────────── */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          order: { xs: 1, md: 2 },
          minHeight: { xs: 230, sm: 280, md: 'auto' },
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'center',
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            width: { xs: 230, sm: 280, md: 360, lg: 400 },
            aspectRatio: '1',
            bottom: { xs: -40, md: -30 },
            left: '50%',
            transform: 'translateX(-50%)',
            borderRadius: '50%',
            background: `radial-gradient(circle at 45% 38%, #F5EBD3 0%, ${GOLD_SOFT} 35%, ${GOLD} 75%, #9C7F45 100%)`,
            boxShadow: '0 0 0 18px rgba(200,169,110,0.14), 0 0 0 40px rgba(200,169,110,0.06)',
          }}
        />
        <Box
          component="img"
          src={TRIPPY_SRC}
          alt={t('hero.trippyAlt')}
          loading="eager"
          sx={{
            position: 'relative',
            display: 'block',
            height: { xs: 220, sm: 270, md: 380, lg: 420 },
            width: 'auto',
            maxWidth: '100%',
            objectFit: 'contain',
            filter: 'drop-shadow(0 18px 30px rgba(0,0,0,0.35))',
            transformOrigin: '50% 100%',
            animation: 'trippyFloat 5s ease-in-out infinite',
            '@keyframes trippyFloat': {
              '0%, 100%': { transform: 'translateY(0) rotate(0deg)' },
              '50%': { transform: 'translateY(-8px) rotate(-1.5deg)' },
            },
            ...NO_MOTION,
          }}
        />


      </Box>
    </Box>
  );
}
